/**
 * MensagensPage — superfície "Mensagens" da mensageria interna entre operadores
 * (Item #8b, Slice C). Layout de dois painéis: esquerda = inbox de threads;
 * direita = pane do thread.
 *
 * Inbox da esquerda (Slice C) + shell + roteamento + seleção URL-driven
 * (`:threadId`). A direita: vazio "Selecione uma conversa" sem seleção, ou a
 * `<ThreadView>` (Slice D) chaveada no threadId quando há thread selecionado.
 *
 * Gate P19 (`direct-message-participate`): sem a cap (ex.: plano Solo) renderiza
 * estado "indisponível" honesto — a mensageria operador↔operador não faz sentido
 * com 1 pessoa (Team+). O item de nav já é ausente sem a cap (Slice B); este gate
 * cobre o acesso por URL direta.
 */

import { Lock, MessageSquare, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useCapability } from '../../hooks/use-capability';
import { useAuthStore } from '../../stores/auth-store';

import { ThreadListItem } from './components/ThreadListItem';
import { ThreadView } from './components/ThreadView';
import { useThreads } from './hooks/use-direct-messages';
import { SendDirectMessageModal } from './SendDirectMessageModal';

import './mensagens.css';

export function MensagensPage(): JSX.Element {
  const canParticipate = useCapability('direct-message-participate');

  if (!canParticipate) {
    return <UnavailableGate />;
  }

  return <MensagensInner />;
}

function MensagensInner(): JSX.Element {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { data: threads, isLoading } = useThreads();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  // CTA "Nova mensagem" com QUALQUER cap de iniciação (P19) — supervisor (down),
  // atendente (up) e lateral/anyone (opt-in). O picker (recipient-scope) mostra só
  // destinatários válidos; leitura/resposta seguem só com participate (gate acima).
  // Hooks chamados incondicionalmente (rules-of-hooks) antes de combinar.
  const canInitiateDown = useCapability('direct-message-initiate');
  const canInitiateUp = useCapability('direct-message-initiate-upward');
  const canInitiateLateral = useCapability('direct-message-initiate-lateral');
  const canInitiateAnyone = useCapability('direct-message-initiate-anyone');
  const canInitiate =
    canInitiateDown || canInitiateUp || canInitiateLateral || canInitiateAnyone;
  const [composeOpen, setComposeOpen] = useState(false);

  return (
    <div className="dm-screen">
      <header className="dm-header">
        <span className="dm-header__eyebrow">
          <Lock size={11} strokeWidth={1.75} aria-hidden />
          Interno · Operadores
        </span>
        <div className="dm-header__row">
          <h1 className="dm-header__title">Mensagens</h1>
          {canInitiate && (
            <button
              type="button"
              className="dm-header__cta"
              onClick={() => {
                setComposeOpen(true);
              }}
            >
              <Plus size={14} strokeWidth={2} aria-hidden />
              Nova mensagem
            </button>
          )}
        </div>
      </header>

      <div className="dm-body">
        <aside className="dm-list-panel" aria-label="Lista de mensagens">
          <div className="dm-list-panel__head">
            <span className="dm-list-panel__title">Conversas internas</span>
          </div>
          <div className="dm-list">
            {isLoading ? (
              <LoadingList />
            ) : threads.length === 0 ? (
              <EmptyList />
            ) : (
              threads.map((thread) => (
                <ThreadListItem
                  key={thread.threadId}
                  thread={thread}
                  currentUserId={currentUserId}
                  selected={thread.threadId === threadId}
                />
              ))
            )}
          </div>
        </aside>

        <section className="dm-pane" aria-label="Conversa selecionada">
          {threadId !== undefined ? (
            <ThreadView key={threadId} threadId={threadId} />
          ) : (
            <EmptySelection />
          )}
        </section>
      </div>

      {composeOpen && (
        <SendDirectMessageModal
          open
          onClose={() => {
            setComposeOpen(false);
          }}
          onSent={(id) => {
            setComposeOpen(false);
            navigate(`/mensagens/${id}`);
          }}
        />
      )}
    </div>
  );
}

function LoadingList(): JSX.Element {
  return (
    <>
      <div className="dm-skeleton" aria-hidden />
      <div className="dm-skeleton" aria-hidden />
      <div className="dm-skeleton" aria-hidden />
      <span className="dm-list-state__title" role="status">
        Carregando mensagens…
      </span>
    </>
  );
}

function EmptyList(): JSX.Element {
  return (
    <div className="dm-list-state">
      <MessageSquare size={28} strokeWidth={1.5} aria-hidden />
      <span className="dm-list-state__title">Nenhuma mensagem ainda</span>
    </div>
  );
}

function EmptySelection(): JSX.Element {
  return (
    <div className="dm-pane__empty">
      <MessageSquare size={40} strokeWidth={1.5} className="dm-pane__empty-icon" aria-hidden />
      <span className="dm-pane__empty-title">Selecione uma conversa</span>
      <span className="dm-pane__empty-sub">Escolha um item à esquerda para ler e responder.</span>
    </div>
  );
}

function UnavailableGate(): JSX.Element {
  return (
    <div className="dm-screen">
      <div className="dm-gate">
        <Lock size={36} strokeWidth={1.5} className="dm-gate__icon" aria-hidden />
        <span className="dm-gate__title">Mensageria interna indisponível</span>
        <span className="dm-gate__sub">
          As mensagens entre operadores estão disponíveis a partir do plano Team. No plano Solo, com
          um único operador, essa superfície não se aplica.
        </span>
      </div>
    </div>
  );
}
